import { Facet, IFacet, pack, IFaceted } from '../Facet';
import { IItemCache } from '../IItemCacheObserver';
import { KeyFacet, stringPart, Key } from '../KeyFacet';
import { createMemoizer } from '../../utilities/Memoizer';

/**
 * @public
 */
export type IControlFaceted = IFaceted<IControl<any, any>>; // tslint:disable-line:no-any

/**
 * @public
 */
export const NO_CONTROL_HANDLERS: IControlFaceted = pack({}, {});

/**
 * Key facet for items used to track control registration.
 * There should be a unique cache item for each combination
 * of control type and target key facet.
 * @internal
 */
export const controlTypeKeyFacet = new KeyFacet<{
    controlTypeId: string;
    keyFacetId: string;
}>('control', {
    controlTypeId: stringPart(),
    keyFacetId: stringPart()
});

/**
 * Key type shared by all control handler injection points.
 * @public
 */
export type ControlTypeKey<TKey extends object, TControlType> = Key<{
    controlTypeId: string;
    keyFacetId: string;
}> & {
    keyType: TKey;
    optionsType: TControlType;
};

/**
 * Standard interface for all controls.
 * Each control type supports custom input props and custom output overrides.
 * @public
 */
export interface IControl<TProps, TOverrides> {
    /**
     * Provides optional overrides for a control.
     * The props are a mix of the default behaviors and required props for the control.
     * A control handler may use the item cache to take dependencies on any facets
     * for items provided via the props.
     * Any fields emitted in the response will override the default behaviors.
     */
    (itemCache: IItemCache, props: TProps & Partial<TOverrides>): Partial<TOverrides>;
}

/**
 * @public
 */
export interface IControlOverrideOption<T> {
    merge(previous: T | undefined, current: T | undefined): T | undefined;
}

/**
 * @public
 */
export type IControlFacetOptions<TOverrides> = {
    [K in keyof TOverrides]?: IControlOverrideOption<TOverrides[K]>;
};

interface IControlHandlerMemoizer<TProps, TOverrides> {
    (current: IControl<TProps, TOverrides>): (
        (previous: IControl<TProps, TOverrides>) => IControl<TProps, TOverrides>
    );
}

/**
 * Creates a new facet which defines the interface for a 'control', which allows
 * addons and host applications to override behaviors in the UX layer.
 * @public
 */
export class ControlFacet<TProps, TOverrides> extends Facet<IControl<TProps, TOverrides>> { // tslint:disable-line:max-line-length no-any
    public readonly propsType!: TProps;
    public readonly overridesType!: TOverrides;
    public readonly inputType!: TProps & Partial<TOverrides>;
    public readonly returnType!: Partial<TOverrides>;

    private _memoizer: IControlHandlerMemoizer<TProps, TOverrides>;

    public get default(): IControl<TProps, TOverrides> { return defaultControlHandler; }

    public static get(id: string): ControlFacet<{}, {}> {
        return Facet.get(id) as ControlFacet<{}, {}>;
    }

    /**
     * Gets the item cache key corresponding to a given control type
     * and key facet pair.
     */
    public static getControlTypeKey<TKey extends object, TControlType>({
        controlTypeFacet,
        keyFacet
    }: {
            controlTypeFacet: IFacet<TControlType>;
            keyFacet: KeyFacet<TKey>;
        }): ControlTypeKey<TKey, TControlType> {
        return controlTypeKeyFacet.serialize({
            controlTypeId: controlTypeFacet.id,
            keyFacetId: keyFacet.id
        }) as ControlTypeKey<TKey, TControlType>;
    }

    constructor(name: string, options: IControlFacetOptions<TOverrides> = {}) {
        super(name);

        this._memoizer = createControlHandlerComposer(options);
    }

    /**
     * Composes together two control handlers, producing a new control handler
     * which emits the result of the inner handler passed through the outer handler.
     */
    public compose(
        inner: IControl<TProps, TOverrides> | undefined,
        outer: IControl<TProps, TOverrides> | undefined
    ): IControl<TProps, TOverrides> | undefined {
        return this._reducer(inner, outer);
    }

    /**
     * Composes together zero or more control handlers from least to most important,
     * and then provides a function to evaluate the set of control handlers with a given
     * set of props and default values
     * @param controlHandlers - Zero or more control handlers to evaluate, from least to most important.
     */
    public evaluate(...controlHandlers: (IControl<TProps, TOverrides> | undefined)[]):
        IControlHandlerWithDefaults<TProps, TOverrides> {
        const handler = controlHandlers.length > 0 &&
            this._reducer(identityControlHandler, controlHandlers.reduce(this._reducer)) ||
            defaultControlHandler;

        function controlHandlerWithDefaults<TDefaults extends Partial<TOverrides>>(
            itemCache: IItemCache,
            props: TProps & TDefaults
        ): TProps & Partial<TOverrides> & TDefaults {
            const overrides = handler(itemCache, props);

            return {
                ...(props as {}),
                ...(overrides as {})
            } as TProps & Partial<TOverrides> & TDefaults;
        }

        // tslint:disable-next-line:no-any
        (<any>controlHandlerWithDefaults).handler = handler;

        return controlHandlerWithDefaults;
    }

    private _reducer = (
        previous: IControl<TProps, TOverrides> | undefined,
        current: IControl<TProps, TOverrides> | undefined
    ): IControl<TProps, TOverrides> | undefined => {
        return current && previous && this._memoizer(current)(previous) || current || previous;
    }
}

const NO_OVERRIDES = {};

/**
 * The 'default' control handler, which provides no overrides.
 * Since every control handler interface allows an empty object as output,
 * this handler should satisfy every contract with the same immutable result.
 * @internal
 */
export function defaultControlHandler(): {} {
    return NO_OVERRIDES;
}

function identityControlHandler<TProps>(itemCache: IItemCache, props: TProps): TProps {
    return props;
}

/**
 * @public
 */
export type IControlHandlerWithDefaults<TProps, TOverrides> = <TDefaults extends Partial<TOverrides>>(
    itemCache: IItemCache,
    props: TProps & TDefaults
) => TProps & Partial<TOverrides> & TDefaults;

/**
 * Defines a control option to compose a child control.
 * @public
 */
export function childControlOption<TProps, TOverrides, TControl extends IControl<TProps, TOverrides>>(
    childControl: ControlFacet<TProps, TOverrides>
): IControlOverrideOption<TControl> {
    return {
        merge(previous: TControl | undefined, current: TControl | undefined): TControl | undefined {
            // @ts-ignore I solemnly swear I am up to no good.
            return childControl.compose(previous, current);
        }
    };
}

function createControlHandlerComposer<TProps, TOverrides>(
    options: IControlFacetOptions<TOverrides>
): IControlHandlerMemoizer<TProps, TOverrides> {
    function getComposerForCurrentHandler(
        current: IControl<TProps, TOverrides>
    ): ReturnType<IControlHandlerMemoizer<TProps, TOverrides>> {
        function getComposedHandler(previous: IControl<TProps, TOverrides>): IControl<TProps, TOverrides> {
            function composedControlHandler(
                itemCache: IItemCache,
                props: TProps & Partial<TOverrides>
            ): Partial<TOverrides> {
                const previousOverrides = previous(itemCache, props) as Partial<TOverrides>;

                const nextOverrides = current(itemCache, {
                    ...(props as {}),
                    ...(previousOverrides as {})
                } as TProps & Partial<TOverrides>);

                const evaluation = {} as Partial<TOverrides>;

                for (const key in nextOverrides) {
                    if (Object.prototype.hasOwnProperty.call(nextOverrides, key)) {
                        const keyOptions = options[key];

                        evaluation[key] = keyOptions && (key in previousOverrides) ?
                            keyOptions.merge(
                                // @ts-ignore
                                previousOverrides[key],
                                nextOverrides[key]
                            ) :
                            nextOverrides[key];
                    }
                }

                return {
                    ...(previousOverrides as {}),
                    ...(evaluation as {})
                } as Partial<TOverrides>;
            }

            // tslint:disable-next-line:no-any
            (<any>composedControlHandler).previous = previous;
            // tslint:disable-next-line:no-any
            (<any>composedControlHandler).current = current;

            return composedControlHandler;
        }

        return createMemoizer(getComposedHandler);
    }

    return createMemoizer(getComposerForCurrentHandler);
}
